import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AlertProvider } from "./context/AlertContext";
import { LogoProvider } from "./context/LogoContext";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import CriticalAlertBanner from "./components/CriticalAlertBanner";

// Pages
import LoginPage from "./pages/LoginPage";
import MunicipalDashboard from "./pages/MunicipalDashboard";
import BarangayDashboard from "./pages/BarangayDashboard";
import ResidentPortal from "./pages/ResidentPortal";
import SettingsPage from "./pages/SettingsPage";

// Modules
import HealthLogbook from "./pages/modules/HealthLogbook";
import MedicineInventory from "./pages/modules/MedicineInventory";
import IncidentManagement from "./pages/modules/IncidentManagement";
import HouseholdManagement from "./pages/modules/HouseholdManagement";
import TaskManagement from "./pages/modules/TaskManagement";
import RequestManagement from "./pages/modules/RequestManagement";
import AnnouncementManagement from "./pages/modules/AnnouncementManagement";
import AuditLogs from "./pages/modules/AuditLogs";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    const roleRoutes = {
      municipal_admin: '/municipal',
      barangay_admin: '/barangay',
      bhw: '/bhw',
      emt: '/emt',
    };
    return <Navigate to={roleRoutes[user.role] || '/login'} replace />;
  }

  return children;
};

// Public Route (redirects if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (user) {
    const roleRoutes = {
      municipal_admin: '/municipal',
      barangay_admin: '/barangay',
      bhw: '/bhw',
      emt: '/emt',
    };
    return <Navigate to={roleRoutes[user.role] || '/'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/r/:qrCode" element={<ResidentPortal />} />

      {/* Municipal Admin Routes */}
      <Route path="/municipal" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <MunicipalDashboard />
        </ProtectedRoute>
      } />
      <Route path="/municipal/barangays" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <MunicipalDashboard />
        </ProtectedRoute>
      } />
      <Route path="/municipal/health" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <HealthLogbook />
        </ProtectedRoute>
      } />
      <Route path="/municipal/medicines" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <MedicineInventory />
        </ProtectedRoute>
      } />
      <Route path="/municipal/incidents" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <IncidentManagement />
        </ProtectedRoute>
      } />
      <Route path="/municipal/households" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <HouseholdManagement />
        </ProtectedRoute>
      } />
      <Route path="/municipal/vaccinations" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <HealthLogbook />
        </ProtectedRoute>
      } />
      <Route path="/municipal/evacuation" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <MunicipalDashboard />
        </ProtectedRoute>
      } />
      <Route path="/municipal/requests" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <RequestManagement />
        </ProtectedRoute>
      } />
      <Route path="/municipal/announcements" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <AnnouncementManagement />
        </ProtectedRoute>
      } />
      <Route path="/municipal/alerts" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <MunicipalDashboard />
        </ProtectedRoute>
      } />
      <Route path="/municipal/audit" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <AuditLogs />
        </ProtectedRoute>
      } />
      <Route path="/municipal/settings" element={
        <ProtectedRoute allowedRoles={['municipal_admin']}>
          <SettingsPage />
        </ProtectedRoute>
      } />

      {/* Barangay Admin Routes */}
      <Route path="/barangay" element={
        <ProtectedRoute allowedRoles={['barangay_admin']}>
          <BarangayDashboard />
        </ProtectedRoute>
      } />
      <Route path="/barangay/health" element={
        <ProtectedRoute allowedRoles={['barangay_admin']}>
          <HealthLogbook />
        </ProtectedRoute>
      } />
      <Route path="/barangay/medicines" element={
        <ProtectedRoute allowedRoles={['barangay_admin']}>
          <MedicineInventory />
        </ProtectedRoute>
      } />
      <Route path="/barangay/incidents" element={
        <ProtectedRoute allowedRoles={['barangay_admin']}>
          <IncidentManagement />
        </ProtectedRoute>
      } />
      <Route path="/barangay/households" element={
        <ProtectedRoute allowedRoles={['barangay_admin']}>
          <HouseholdManagement />
        </ProtectedRoute>
      } />
      <Route path="/barangay/tasks" element={
        <ProtectedRoute allowedRoles={['barangay_admin']}>
          <TaskManagement />
        </ProtectedRoute>
      } />
      <Route path="/barangay/vaccinations" element={
        <ProtectedRoute allowedRoles={['barangay_admin']}>
          <HealthLogbook />
        </ProtectedRoute>
      } />
      <Route path="/barangay/evacuation" element={
        <ProtectedRoute allowedRoles={['barangay_admin']}>
          <BarangayDashboard />
        </ProtectedRoute>
      } />
      <Route path="/barangay/requests" element={
        <ProtectedRoute allowedRoles={['barangay_admin']}>
          <RequestManagement />
        </ProtectedRoute>
      } />
      <Route path="/barangay/announcements" element={
        <ProtectedRoute allowedRoles={['barangay_admin']}>
          <AnnouncementManagement />
        </ProtectedRoute>
      } />
      <Route path="/barangay/alerts" element={
        <ProtectedRoute allowedRoles={['barangay_admin']}>
          <BarangayDashboard />
        </ProtectedRoute>
      } />

      {/* BHW Routes */}
      <Route path="/bhw" element={
        <ProtectedRoute allowedRoles={['bhw']}>
          <BarangayDashboard />
        </ProtectedRoute>
      } />
      <Route path="/bhw/tasks" element={
        <ProtectedRoute allowedRoles={['bhw']}>
          <TaskManagement />
        </ProtectedRoute>
      } />
      <Route path="/bhw/health" element={
        <ProtectedRoute allowedRoles={['bhw']}>
          <HealthLogbook />
        </ProtectedRoute>
      } />
      <Route path="/bhw/vaccinations" element={
        <ProtectedRoute allowedRoles={['bhw']}>
          <HealthLogbook />
        </ProtectedRoute>
      } />
      <Route path="/bhw/households" element={
        <ProtectedRoute allowedRoles={['bhw']}>
          <HouseholdManagement />
        </ProtectedRoute>
      } />

      {/* EMT Routes */}
      <Route path="/emt" element={
        <ProtectedRoute allowedRoles={['emt']}>
          <IncidentManagement />
        </ProtectedRoute>
      } />
      <Route path="/emt/incidents" element={
        <ProtectedRoute allowedRoles={['emt']}>
          <IncidentManagement />
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LogoProvider>
          <AlertProvider>
            <BrowserRouter>
              <CriticalAlertBanner />
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </AlertProvider>
        </LogoProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
